import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Badge
} from '../../components/UI';
import MainLayout from '../Layout/MainLayout';
import apiClient from '../../utils/api';
import {
  ArrowLeft, ArrowRight, BarChart3, Plus, Trash2, Check, CheckCircle,
  Wand2, Edit3, DollarSign, RefreshCw, X, Save, FileSpreadsheet
} from 'lucide-react';
import BudgetV2AIChat from './BudgetV2AIChat';
import BudgetV2ExcelUpload from './BudgetV2ExcelUpload';

const BudgetAgentV2New = () => {
  const navigate = useNavigate();

  // Form state
  const [budgetName, setBudgetName] = useState('');
  const [fiscalYear, setFiscalYear] = useState('2026');
  const [description, setDescription] = useState('');
  const [subsidiaryId, setSubsidiaryId] = useState('');
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [loadingSubsidiaries, setLoadingSubsidiaries] = useState(false);

  // Mode: 'manual' or 'ai'
  const [mode, setMode] = useState(null);

  // Categories state
  const [predefinedCategories, setPredefinedCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);

  // Budget creation state
  const [budgetId, setBudgetId] = useState(null);
  const [step, setStep] = useState(1); // 1: Basic info, 2: Mode selection, 3: Categories, 4: Expenses

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);

  // AI categorization state
  const [aiCategorizedResults, setAiCategorizedResults] = useState(null);

  // New custom category form
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');

  // Add expense to category
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseCategoryId, setExpenseCategoryId] = useState(null);
  const [expenseAccounts, setExpenseAccounts] = useState({ summary: [], detail: [], all: [] });
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [showAccountType, setShowAccountType] = useState('all');

  // Financial Summary Budgets (Income & COGS)
  const [incomeBudgets, setIncomeBudgets] = useState({
    jan: '', feb: '', mar: '', apr: '', may: '', jun: '',
    jul: '', aug: '', sep: '', oct: '', nov: '', dec: ''
  });
  const [cogsBudgets, setCogsBudgets] = useState({
    jan: '', feb: '', mar: '', apr: '', may: '', jun: '',
    jul: '', aug: '', sep: '', oct: '', nov: '', dec: ''
  });

  useEffect(() => {
    fetchPredefinedCategories();
    fetchSubsidiaries();
  }, []);

  const fetchSubsidiaries = async () => {
    try {
      setLoadingSubsidiaries(true);
      const response = await apiClient.get('/api/budget-v2/netsuite/subsidiaries');
      if (response && response.subsidiaries) {
        setSubsidiaries(response.subsidiaries);
      }
    } catch (error) {
      console.error('Error fetching subsidiaries:', error);
    } finally {
      setLoadingSubsidiaries(false);
    }
  };

  const fetchPredefinedCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await apiClient.get('/api/budget-v2/categories/predefined');
      if (response && response.categories) {
        setPredefinedCategories(response.categories);
      }
    } catch (error) {
      console.error('Error fetching predefined categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const createBudget = async () => {
    if (!budgetName.trim() || !fiscalYear) {
      alert('Please fill in the budget name and fiscal year');
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post('/api/budget-v2/budgets', {
        name: budgetName,
        fiscal_year: fiscalYear,
        description
      });

      if (response && response.budget_id) {
        setBudgetId(response.budget_id);
        setStep(2);
      }
    } catch (error) {
      console.error('Error creating budget:', error);
      alert('Failed to create budget');
    } finally {
      setLoading(false);
    }
  };

  const selectMode = async (selectedMode) => {
    setMode(selectedMode);

    if (selectedMode === 'ai') {
      // For AI mode, go to chat interface (step 5)
      setStep(5);
    } else if (selectedMode === 'excel') {
      // For Excel import mode, go to Excel upload interface (step 6)
      setStep(6);
    } else {
      // For manual mode, fetch expense accounts first then go to step 3
      setSelectedCategories([]);
      await fetchExpenseAccounts(); // Pre-fetch expense accounts for auto-selection
      setStep(3);
    }
  };

  const toggleCategorySelection = (category) => {
    const exists = selectedCategories.find(c => c.id === category.id);

    if (exists) {
      setSelectedCategories(selectedCategories.filter(c => c.id !== category.id));
    } else {
      // For predefined categories, auto-add the matching expense account
      let expenses = [];
      if (category.account_number && category.account_number !== 'custom') {
        // Find matching expense account from NetSuite by account number
        // Convert both to string for comparison (handles number vs string mismatch)
        const categoryAcctNum = String(category.account_number).trim();
        const matchingAccount = expenseAccounts.all.find(
          acc => String(acc.acctnumber).trim() === categoryAcctNum
        );

        console.log(`[Budget] Category: ${category.name}, Account: ${categoryAcctNum}, Found: ${matchingAccount ? matchingAccount.acctname : 'NOT FOUND'}`);

        if (matchingAccount) {
          expenses = [{
            id: `exp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            account_id: matchingAccount.id,
            acctnumber: matchingAccount.acctnumber,
            acctname: matchingAccount.acctname,
            accttype: matchingAccount.accttype,
            amount: 0
          }];
        }
      }
      setSelectedCategories([...selectedCategories, { ...category, budgetAmount: 0, expenses }]);
    }
  };

  const addCustomCategory = () => {
    if (!newCategoryName.trim()) {
      alert('Please enter a category name');
      return;
    }

    const newCategory = {
      id: `custom-${Date.now()}`,
      name: newCategoryName,
      description: newCategoryDescription,
      account_number: 'custom',
      isCustom: true,
      budgetAmount: 0
    };

    setCustomCategories([...customCategories, newCategory]);
    setSelectedCategories([...selectedCategories, newCategory]);
    setNewCategoryName('');
    setNewCategoryDescription('');
    setShowAddCategory(false);
  };

  const removeCustomCategory = (categoryId) => {
    setCustomCategories(customCategories.filter(c => c.id !== categoryId));
    setSelectedCategories(selectedCategories.filter(c => c.id !== categoryId));
  };

  const fetchExpenseAccounts = async () => {
    if (expenseAccounts.all.length > 0) return; // Already fetched
    try {
      setLoadingAccounts(true);
      const response = await apiClient.get('/api/budget-v2/netsuite/expense-accounts/all');
      if (response && response.success) {
        setExpenseAccounts({
          summary: response.summary_accounts || [],
          detail: response.detail_accounts || [],
          all: response.all_accounts || []
        });
      }
    } catch (error) {
      console.error('Error fetching expense accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const getFilteredAccounts = () => {
    let accounts = [];
    if (showAccountType === 'summary') {
      accounts = expenseAccounts.summary;
    } else if (showAccountType === 'detail') {
      accounts = expenseAccounts.detail;
    } else {
      accounts = expenseAccounts.all;
    }

    if (!accountSearch.trim()) return accounts;

    const search = accountSearch.toLowerCase();
    return accounts.filter(acc =>
      (acc.acctname || '').toLowerCase().includes(search) ||
      (acc.acctnumber || '').toLowerCase().includes(search)
    );
  };

  const openAddExpenseModal = (categoryId) => {
    setExpenseCategoryId(categoryId);
    setShowAddExpense(true);
    fetchExpenseAccounts();
  };

  const addExpenseToCategory = (account) => {
    const expense = {
      id: `exp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      account_id: account.id,
      acctnumber: account.acctnumber,
      acctname: account.acctname,
      accttype: account.accttype,
      amount: 0,
      jan_budget: 0, feb_budget: 0, mar_budget: 0,
      apr_budget: 0, may_budget: 0, jun_budget: 0,
      jul_budget: 0, aug_budget: 0, sep_budget: 0,
      oct_budget: 0, nov_budget: 0, dec_budget: 0
    };

    setSelectedCategories(selectedCategories.map(cat => {
      if (cat.id === expenseCategoryId) {
        const expenses = cat.expenses || [];
        // Check if already added
        if (expenses.some(e => e.account_id === account.id)) {
          return cat;
        }
        return { ...cat, expenses: [...expenses, expense] };
      }
      return cat;
    }));

    setAccountSearch('');
  };

  const removeExpenseFromCategory = (categoryId, expenseId) => {
    setSelectedCategories(selectedCategories.map(cat => {
      if (cat.id === categoryId) {
        return { ...cat, expenses: (cat.expenses || []).filter(e => e.id !== expenseId) };
      }
      return cat;
    }));
  };

  const updateExpenseAmount = (categoryId, expenseId, field, value) => {
    setSelectedCategories(selectedCategories.map(cat => {
      if (cat.id === categoryId) {
        return {
          ...cat,
          expenses: (cat.expenses || []).map(e => {
            if (e.id === expenseId) {
              const updated = { ...e, [field]: parseFloat(value) || 0 };
              // Recalculate total amount from monthly budgets
              updated.amount = ['jan_budget', 'feb_budget', 'mar_budget', 'apr_budget',
                'may_budget', 'jun_budget', 'jul_budget', 'aug_budget',
                'sep_budget', 'oct_budget', 'nov_budget', 'dec_budget']
                .reduce((sum, k) => sum + (parseFloat(updated[k]) || 0), 0);
              return updated;
            }
            return e;
          })
        };
      }
      return cat;
    }));
  };

  const getCategoryExpenseTotal = (category) => {
    return (category.expenses || []).reduce((sum, e) => sum + (e.amount || 0), 0);
  };

  const updateCategoryBudget = (categoryId, amount) => {
    setSelectedCategories(selectedCategories.map(c =>
      c.id === categoryId ? { ...c, budgetAmount: parseFloat(amount) || 0 } : c
    ));
  };

  const saveCategories = async () => {
    if (selectedCategories.length === 0) {
      alert('Please select at least one category');
      return;
    }

    try {
      setLoading(true);

      // Convert selected categories to API format with expenses including monthly budgets
      // Note: expense amounts are already stored in actual dollars (converted when entered)
      const categoriesToCreate = selectedCategories.map(cat => ({
        name: cat.name,
        description: cat.description || '',
        budget_amount: getCategoryExpenseTotal(cat) || cat.budgetAmount || 0,
        expenses: (cat.expenses || []).map(exp => ({
          account_id: exp.account_id,
          account_number: exp.acctnumber,
          account_name: exp.acctname,
          amount: exp.amount || 0,
          jan_budget: exp.jan_budget || 0,
          feb_budget: exp.feb_budget || 0,
          mar_budget: exp.mar_budget || 0,
          apr_budget: exp.apr_budget || 0,
          may_budget: exp.may_budget || 0,
          jun_budget: exp.jun_budget || 0,
          jul_budget: exp.jul_budget || 0,
          aug_budget: exp.aug_budget || 0,
          sep_budget: exp.sep_budget || 0,
          oct_budget: exp.oct_budget || 0,
          nov_budget: exp.nov_budget || 0,
          dec_budget: exp.dec_budget || 0
        }))
      }));

      const response = await apiClient.post(`/api/budget-v2/budgets/${budgetId}/categories/bulk`, categoriesToCreate);

      if (response && response.success) {
        // Save Income/COGS budgets if any values are entered
        const toDollars = (val) => (parseFloat(val) || 0) * 1000000;
        const hasIncomeBudget = Object.values(incomeBudgets).some(v => parseFloat(v) > 0);
        const hasCogsBudget = Object.values(cogsBudgets).some(v => parseFloat(v) > 0);

        if (hasIncomeBudget || hasCogsBudget) {
          try {
            await apiClient.put(`/api/budget-v2/budgets/${budgetId}/financial-summary`, {
              income_jan: toDollars(incomeBudgets.jan),
              income_feb: toDollars(incomeBudgets.feb),
              income_mar: toDollars(incomeBudgets.mar),
              income_apr: toDollars(incomeBudgets.apr),
              income_may: toDollars(incomeBudgets.may),
              income_jun: toDollars(incomeBudgets.jun),
              income_jul: toDollars(incomeBudgets.jul),
              income_aug: toDollars(incomeBudgets.aug),
              income_sep: toDollars(incomeBudgets.sep),
              income_oct: toDollars(incomeBudgets.oct),
              income_nov: toDollars(incomeBudgets.nov),
              income_dec: toDollars(incomeBudgets.dec),
              cogs_jan: toDollars(cogsBudgets.jan),
              cogs_feb: toDollars(cogsBudgets.feb),
              cogs_mar: toDollars(cogsBudgets.mar),
              cogs_apr: toDollars(cogsBudgets.apr),
              cogs_may: toDollars(cogsBudgets.may),
              cogs_jun: toDollars(cogsBudgets.jun),
              cogs_jul: toDollars(cogsBudgets.jul),
              cogs_aug: toDollars(cogsBudgets.aug),
              cogs_sep: toDollars(cogsBudgets.sep),
              cogs_oct: toDollars(cogsBudgets.oct),
              cogs_nov: toDollars(cogsBudgets.nov),
              cogs_dec: toDollars(cogsBudgets.dec)
            });
          } catch (err) {
            console.error('Error saving financial summary budgets:', err);
          }
        }

        // Navigate to budget detail page
        navigate(`/budget-agent-v2/${budgetId}`);
      }
    } catch (error) {
      console.error('Error saving categories:', error);
      alert('Failed to save categories');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalBudget = () => {
    // Sum up expense amounts for each category (or fallback to budgetAmount)
    return selectedCategories.reduce((sum, cat) => {
      const expenseTotal = getCategoryExpenseTotal(cat);
      return sum + (expenseTotal > 0 ? expenseTotal : (cat.budgetAmount || 0));
    }, 0);
  };

  const runAICategorization = async () => {
    if (selectedCategories.length === 0) {
      alert('Please select at least one category for AI to categorize into');
      return;
    }

    try {
      setLoadingAI(true);

      // Call AI categorization endpoint
      const response = await apiClient.post('/api/budget-v2/ai/categorize', {
        categories: selectedCategories.map(cat => ({
          id: cat.id,
          name: cat.name,
          description: cat.description || '',
          account_number: cat.account_number
        }))
      });

      if (response && response.success) {
        setAiCategorizedResults(response.results);
        setStep(4); // Move to AI results step
      } else {
        alert(response?.message || 'AI categorization failed');
      }
    } catch (error) {
      console.error('Error running AI categorization:', error);
      alert('Failed to run AI categorization: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingAI(false);
    }
  };

  const saveAICategorizedBudget = async () => {
    if (!aiCategorizedResults) {
      alert('No categorization results to save');
      return;
    }

    try {
      setLoading(true);

      // Convert AI results to categories with accounts
      const categoriesToCreate = aiCategorizedResults.categorized.map(cat => ({
        name: cat.category_name,
        description: cat.category_description || '',
        budget_amount: 0, // User can set this later
        accounts: cat.accounts.map(acc => ({
          account_id: acc.id,
          account_number: acc.acctnumber,
          account_name: acc.acctname
        }))
      }));

      // Save categories in bulk
      const response = await apiClient.post(`/api/budget-v2/budgets/${budgetId}/categories/bulk`, categoriesToCreate);

      if (response && response.success) {
        // Navigate to budget detail page
        navigate(`/budget-agent-v2/${budgetId}`);
      }
    } catch (error) {
      console.error('Error saving AI categorized budget:', error);
      alert('Failed to save budget');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const renderStep1 = () => (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-emerald-500" />
          Create New Budget
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Budget Name *
          </label>
          <input
            type="text"
            value={budgetName}
            onChange={(e) => setBudgetName(e.target.value)}
            placeholder="e.g., Q1 2026 Operating Budget"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Fiscal Year *
          </label>
          <select
            value={fiscalYear}
            onChange={(e) => setFiscalYear(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="2026">2026</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Subsidiary (Optional)
          </label>
          <select
            value={subsidiaryId}
            onChange={(e) => setSubsidiaryId(e.target.value)}
            disabled={loadingSubsidiaries}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50"
          >
            <option value="">All Subsidiaries</option>
            {subsidiaries.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.name}
              </option>
            ))}
          </select>
          {loadingSubsidiaries && (
            <p className="text-xs text-gray-500 mt-1">Loading subsidiaries...</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description (optional)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of this budget..."
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => navigate('/budget-agent-v2')}
          >
            Cancel
          </Button>
          <Button
            onClick={createBudget}
            disabled={loading || !budgetName.trim()}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep2 = () => (
    <Card className="max-w-5xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-emerald-500" />
          Choose Budget Creation Mode
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-gray-600 dark:text-gray-400">
          How would you like to set up your budget categories?
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Manual Mode */}
          <div
            className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-emerald-500 cursor-pointer transition-all group"
            onClick={() => selectMode('manual')}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg group-hover:bg-blue-200 dark:group-hover:bg-blue-800">
                <Edit3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Manual Mode
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
              Select categories from predefined list and add your own custom categories.
              Full control over category selection and budget amounts.
            </p>
            <ul className="text-sm text-gray-500 space-y-2">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Choose from predefined categories
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Add custom categories
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Set individual budget amounts
              </li>
            </ul>
          </div>

          {/* AI Mode */}
          <div
            className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-emerald-500 cursor-pointer transition-all group"
            onClick={() => selectMode('ai')}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg group-hover:bg-purple-200 dark:group-hover:bg-purple-800">
                <Wand2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                AI-Assisted Mode
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
              AI will automatically categorize your expense accounts into the selected categories.
              Perfect for quick setup with intelligent account assignment.
            </p>
            <ul className="text-sm text-gray-500 space-y-2">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Auto-categorize expense accounts
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Add custom categories if needed
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Review and modify AI suggestions
              </li>
            </ul>
          </div>

          {/* Excel Import Mode */}
          <div
            className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-emerald-500 cursor-pointer transition-all group relative"
            onClick={() => selectMode('excel')}
          >
            <div className="absolute top-2 right-2">
              <Badge className="bg-amber-100 text-amber-700 text-xs">Recommended</Badge>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900 rounded-lg group-hover:bg-emerald-200 dark:group-hover:bg-emerald-800">
                <FileSpreadsheet className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Import from Excel
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
              Upload your existing budget Excel file. AI will parse and map categories to NetSuite accounts automatically.
            </p>
            <ul className="text-sm text-gray-500 space-y-2">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Upload existing budget Excel
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                AI maps to NetSuite accounts
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Review mappings before saving
              </li>
            </ul>
          </div>
        </div>

        <div className="flex justify-start pt-4">
          <Button
            variant="outline"
            onClick={() => setStep(1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep3 = () => (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                {mode === 'manual' ? (
                  <>
                    <Edit3 className="w-5 h-5 text-blue-500" />
                    Manual Category Selection
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5 text-purple-500" />
                    AI-Assisted Setup
                  </>
                )}
              </h2>
              <p className="text-sm text-gray-500">
                Budget: <span className="font-medium">{budgetName}</span> |
                Fiscal Year: <span className="font-medium">{fiscalYear}</span>
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Total Budget</p>
                <p className="text-xl font-bold text-emerald-600">
                  {formatCurrency(calculateTotalBudget())}
                </p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                {selectedCategories.length} Categories Selected
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Predefined Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Predefined Categories</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddCategory(true)}
                className="text-emerald-600"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Custom
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto">
            {loadingCategories || loadingAccounts ? (
              <div className="flex flex-col items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                <p className="text-sm text-gray-500 mt-2">
                  {loadingAccounts ? 'Loading expense accounts...' : 'Loading categories...'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {predefinedCategories.map((category) => {
                  const isSelected = selectedCategories.some(c => c.id === category.id);

                  return (
                    <div
                      key={category.id}
                      onClick={() => toggleCategorySelection(category)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {category.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              Account: {category.account_number}
                              {/* Show if matching account exists in NetSuite */}
                              {expenseAccounts.all.length > 0 && (
                                expenseAccounts.all.some(acc => String(acc.acctnumber).trim() === String(category.account_number).trim())
                                  ? <span className="ml-2 text-emerald-500">✓ Found</span>
                                  : <span className="ml-2 text-amber-500">⚠ Not in NetSuite</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                      {category.description && (
                        <p className="text-sm text-gray-500 mt-2 ml-8">
                          {category.description}
                        </p>
                      )}
                    </div>
                  );
                })}

                {/* Custom Categories */}
                {customCategories.map((category) => {
                  const isSelected = selectedCategories.some(c => c.id === category.id);

                  return (
                    <div
                      key={category.id}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-5 h-5 rounded-full border-2 border-blue-500 bg-blue-500 flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                              {category.name}
                              <Badge className="bg-blue-100 text-blue-700 text-xs">Custom</Badge>
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCustomCategory(category.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected Categories with Budget */}
        <Card>
          <CardHeader>
            <CardTitle>Selected Categories & Expenses</CardTitle>
          </CardHeader>
          <CardContent className="max-h-[600px] overflow-y-auto">
            {selectedCategories.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                <p>No categories selected</p>
                <p className="text-sm">Select categories from the list</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedCategories.map((category) => (
                  <div
                    key={category.id}
                    className="p-3 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {category.name}
                        {category.isCustom && (
                          <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">Custom</Badge>
                        )}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-emerald-600 font-medium">
                          {formatCurrency(getCategoryExpenseTotal(category))}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCategorySelection(category)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Expense Items */}
                    {category.expenses && category.expenses.length > 0 && (
                      <div className="mt-2 space-y-3 pl-2 border-l-2 border-emerald-200">
                        {category.expenses.map((expense) => (
                          <div key={expense.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {expense.acctname}
                                <span className="text-gray-400 ml-1 text-xs">({expense.acctnumber})</span>
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">
                                  Total: {((expense.amount || 0) / 1000000).toFixed(2)}M
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeExpenseFromCategory(category.id, expense.id)}
                                  className="text-red-400 hover:text-red-600 p-1"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            {/* Monthly Budget Grid - Values in $M (millions) */}
                            <div className="mb-1">
                              <span className="text-xs text-emerald-600 font-medium">Monthly Budget ($M)</span>
                            </div>
                            <div className="grid grid-cols-6 gap-2">
                              {[
                                { key: 'jan_budget', label: 'Jan' },
                                { key: 'feb_budget', label: 'Feb' },
                                { key: 'mar_budget', label: 'Mar' },
                                { key: 'apr_budget', label: 'Apr' },
                                { key: 'may_budget', label: 'May' },
                                { key: 'jun_budget', label: 'Jun' },
                                { key: 'jul_budget', label: 'Jul' },
                                { key: 'aug_budget', label: 'Aug' },
                                { key: 'sep_budget', label: 'Sep' },
                                { key: 'oct_budget', label: 'Oct' },
                                { key: 'nov_budget', label: 'Nov' },
                                { key: 'dec_budget', label: 'Dec' }
                              ].map(({ key, label }) => (
                                <div key={key}>
                                  <label className="block text-xs text-gray-400 mb-0.5">{label}</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={expense[key] ? (expense[key] / 1000000).toFixed(2) : ''}
                                    onChange={(e) => updateExpenseAmount(category.id, expense.id, key, (parseFloat(e.target.value) || 0) * 1000000)}
                                    placeholder="0.00"
                                    className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Expense Button */}
                    {mode === 'manual' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openAddExpenseModal(category.id)}
                        className="mt-2 text-emerald-600 hover:text-emerald-700 text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Expense Account
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Financial Summary Budgets - Income & COGS */}
      <Card className="border-2 border-indigo-200 dark:border-indigo-800">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
          <CardTitle className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
            <DollarSign className="w-5 h-5" />
            Financial Summary Budgets (Optional)
          </CardTitle>
          <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-1">
            Set monthly budget targets for Income and Cost of Sales. These will be used for Budget vs Actual reporting.
          </p>
        </CardHeader>
        <CardContent className="space-y-6 p-6">
          {/* Income Budget Row */}
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                Total Income Budget <span className="text-xs font-normal text-emerald-600">($M)</span>
              </h3>
              <span className="text-xs font-medium text-emerald-600 bg-emerald-100 dark:bg-emerald-800 px-2 py-0.5 rounded-full">
                Total: {Object.values(incomeBudgets).reduce((sum, v) => sum + (parseFloat(v) || 0), 0).toFixed(2)}M
              </span>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].map((month) => (
                <div key={`income-${month}`}>
                  <label className="block text-xs font-medium text-emerald-700 dark:text-emerald-400 mb-0.5 uppercase">{month}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={incomeBudgets[month]}
                    onChange={(e) => setIncomeBudgets(prev => ({ ...prev, [month]: e.target.value }))}
                    className="w-full px-1.5 py-1.5 text-xs border border-emerald-300 rounded focus:ring-1 focus:ring-emerald-500 bg-white dark:bg-gray-800 dark:border-emerald-600"
                    placeholder="0.00"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* COGS Budget Row */}
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-orange-800 dark:text-orange-300 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>
                Cost of Sales (COGS) Budget <span className="text-xs font-normal text-orange-600">($M)</span>
              </h3>
              <span className="text-xs font-medium text-orange-600 bg-orange-100 dark:bg-orange-800 px-2 py-0.5 rounded-full">
                Total: {Object.values(cogsBudgets).reduce((sum, v) => sum + (parseFloat(v) || 0), 0).toFixed(2)}M
              </span>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].map((month) => (
                <div key={`cogs-${month}`}>
                  <label className="block text-xs font-medium text-orange-700 dark:text-orange-400 mb-0.5 uppercase">{month}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={cogsBudgets[month]}
                    onChange={(e) => setCogsBudgets(prev => ({ ...prev, [month]: e.target.value }))}
                    className="w-full px-1.5 py-1.5 text-xs border border-orange-300 rounded focus:ring-1 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:border-orange-600"
                    placeholder="0.00"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Calculated Summary */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 mb-0.5">Gross Profit Budget</p>
              <p className="text-lg font-bold text-blue-600">
                ${(Object.values(incomeBudgets).reduce((sum, v) => sum + (parseFloat(v) || 0), 0) -
                  Object.values(cogsBudgets).reduce((sum, v) => sum + (parseFloat(v) || 0), 0)).toFixed(2)}M
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 mb-0.5">GP Margin %</p>
              <p className="text-lg font-bold text-purple-600">
                {(() => {
                  const income = Object.values(incomeBudgets).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
                  const cogs = Object.values(cogsBudgets).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
                  return income > 0 ? (((income - cogs) / income) * 100).toFixed(1) : '0.0';
                })()}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setStep(2)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex gap-3">
              {mode === 'ai' ? (
                <Button
                  onClick={runAICategorization}
                  disabled={loadingAI || selectedCategories.length === 0}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {loadingAI ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      AI Categorizing...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Run AI Categorization
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={saveCategories}
                  disabled={loading || selectedCategories.length === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Budget
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Custom Category Modal */}
      {showAddCategory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-4xl">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Add Custom Category</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddCategory(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Training & Development"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={newCategoryDescription}
                  onChange={(e) => setNewCategoryDescription(e.target.value)}
                  placeholder="Brief description..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowAddCategory(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={addCustomCategory}
                  disabled={!newCategoryName.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Add Category
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Expense Modal */}
      {showAddExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-5xl max-h-[80vh] overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Add Expense Account</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddExpense(false);
                    setExpenseCategoryId(null);
                    setAccountSearch('');
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Account Type Filter */}
              <div>
                <label className="block text-sm font-medium mb-2">Account Type</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAccountType('all')}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      showAccountType === 'all'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    All ({expenseAccounts.all.length})
                  </button>
                  <button
                    onClick={() => setShowAccountType('summary')}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      showAccountType === 'summary'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    Parent ({expenseAccounts.summary.length})
                  </button>
                  <button
                    onClick={() => setShowAccountType('detail')}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      showAccountType === 'detail'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    Child ({expenseAccounts.detail.length})
                  </button>
                </div>
              </div>

              {/* Search Accounts */}
              <div>
                <label className="block text-sm font-medium mb-1">Search Expense Accounts</label>
                <input
                  type="text"
                  value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600"
                  placeholder="Search by account name or number..."
                />
              </div>

              {/* Account List */}
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {loadingAccounts ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Loading accounts...</span>
                  </div>
                ) : getFilteredAccounts().length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {expenseAccounts.all.length === 0
                      ? 'No expense accounts found. Check NetSuite connection.'
                      : 'No accounts match your search.'}
                  </div>
                ) : (
                  <div className="divide-y dark:divide-gray-700">
                    {getFilteredAccounts().slice(0, 100).map((account) => {
                      const currentCategory = selectedCategories.find(c => c.id === expenseCategoryId);
                      const isAdded = currentCategory?.expenses?.some(e => e.account_id === account.id);

                      return (
                        <button
                          key={account.id}
                          onClick={() => !isAdded && addExpenseToCategory(account)}
                          disabled={isAdded}
                          className={`w-full px-3 py-2 text-left transition-colors ${
                            isAdded
                              ? 'bg-gray-100 dark:bg-gray-800 opacity-50 cursor-not-allowed'
                              : 'hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white text-sm">
                                {account.acctname}
                              </p>
                              <p className="text-xs text-gray-500">
                                {account.acctnumber} • {account.accttype}
                                {account.account_level === 'summary' && (
                                  <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">Summary</Badge>
                                )}
                              </p>
                            </div>
                            {isAdded ? (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Added</Badge>
                            ) : (
                              <Plus className="w-4 h-4 text-emerald-500" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                    {getFilteredAccounts().length > 100 && (
                      <p className="text-center py-2 text-xs text-gray-500">
                        Showing first 100 results. Refine your search.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddExpense(false);
                    setExpenseCategoryId(null);
                    setAccountSearch('');
                  }}
                >
                  Done
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-purple-500" />
                AI Categorization Results
              </h2>
              <p className="text-sm text-gray-500">
                Budget: <span className="font-medium">{budgetName}</span> |
                Review the AI-categorized expense accounts
              </p>
            </div>
            {aiCategorizedResults && (
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Total Accounts</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {aiCategorizedResults.summary?.total_accounts || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Categorized</p>
                  <p className="text-lg font-bold text-emerald-600">
                    {aiCategorizedResults.summary?.categorized_count || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Uncategorized</p>
                  <p className="text-lg font-bold text-amber-600">
                    {aiCategorizedResults.summary?.uncategorized_count || 0}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Categorized Results */}
      {aiCategorizedResults?.categorized?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            Categorized Accounts ({aiCategorizedResults.summary?.categories_used || 0} categories)
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {aiCategorizedResults.categorized.map((category, idx) => (
              <Card key={idx} className="border-l-4 border-l-emerald-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{category.category_name}</span>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      {category.account_count} accounts
                    </Badge>
                  </CardTitle>
                  {category.category_description && (
                    <p className="text-sm text-gray-500">{category.category_description}</p>
                  )}
                </CardHeader>
                <CardContent className="max-h-48 overflow-y-auto">
                  <div className="space-y-1">
                    {category.accounts.map((account, aIdx) => (
                      <div
                        key={aIdx}
                        className="text-sm p-2 bg-gray-50 dark:bg-gray-800 rounded flex justify-between items-center"
                      >
                        <span className="text-gray-900 dark:text-white">
                          {account.acctname}
                        </span>
                        <span className="text-gray-500 text-xs">
                          {account.acctnumber}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Uncategorized Accounts */}
      {aiCategorizedResults?.uncategorized?.length > 0 && (
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <span className="text-amber-600">Uncategorized Accounts</span>
              <Badge className="bg-amber-100 text-amber-700">
                {aiCategorizedResults.uncategorized.length} accounts
              </Badge>
            </CardTitle>
            <p className="text-sm text-gray-500">
              These accounts couldn't be matched to any category
            </p>
          </CardHeader>
          <CardContent className="max-h-64 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {aiCategorizedResults.uncategorized.map((account, idx) => (
                <div
                  key={idx}
                  className="text-sm p-2 bg-amber-50 dark:bg-amber-900/20 rounded flex justify-between items-center"
                >
                  <span className="text-gray-900 dark:text-white">
                    {account.acctname}
                  </span>
                  <span className="text-gray-500 text-xs">
                    {account.acctnumber}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => {
                setAiCategorizedResults(null);
                setStep(3);
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Categories
            </Button>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={runAICategorization}
                disabled={loadingAI}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingAI ? 'animate-spin' : ''}`} />
                Re-run AI
              </Button>
              <Button
                onClick={saveAICategorizedBudget}
                disabled={loading || !aiCategorizedResults?.categorized?.length}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Budget
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <MainLayout>
      <div className="p-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-sm">
          <button
            onClick={() => navigate('/budget-agent-v2')}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Budget Agent V2
          </button>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 dark:text-white font-medium">New Budget</span>
        </div>

        {/* Steps indicator - only show for steps 1-4 (not AI chat or Excel upload) */}
        {step !== 5 && step !== 6 && (
          <div className="flex items-center justify-center gap-4 mb-8">
            {[1, 2, 3].map((s, index, arr) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step >= s
                    ? 'bg-emerald-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                }`}>
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {index < arr.length - 1 && (
                  <div className={`w-16 h-1 mx-2 ${
                    step > s ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step content */}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && (
          <BudgetV2AIChat
            budgetId={budgetId}
            budgetName={budgetName}
            fiscalYear={fiscalYear}
            subsidiaryId={subsidiaryId}
            subsidiaryName={subsidiaries.find(s => s.id === subsidiaryId)?.name || 'All Subsidiaries'}
            onBack={() => setStep(2)}
            onSave={() => navigate(`/budget-agent-v2/${budgetId}`)}
          />
        )}
        {step === 6 && (
          <BudgetV2ExcelUpload
            budgetId={budgetId}
            budgetName={budgetName}
            fiscalYear={fiscalYear}
            onBack={() => setStep(2)}
            onComplete={() => navigate(`/budget-agent-v2/${budgetId}`)}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default BudgetAgentV2New;
